/**
 * wc-mega-menu.js
 *
 * Two responsibilities:
 *
 * 1. DESKTOP – Products panel coordination.
 *    For the "products" mega-menu layout the product panels live in a sibling
 *    .wc-mm-products__panels wrapper (separate from the <ul>), so pure CSS
 *    :hover on the <li> can no longer reach them. This script adds hover
 *    listeners to sync the active panel.
 *
 * 2. MOBILE – Panel relocation.
 *    Relocates the server-rendered mobile mega-menu panels (emitted once by
 *    sections/header.liquid into `.wc-mm-mobile-panels`) into the matching item
 *    of the mobile navigation drawer.
 *
 *    The panels are rendered by the same wc-mega-menu-* snippets as the desktop
 *    mega-menu, from the same Header blocks and the same navigation linklist,
 *    so the drawer shows the desktop hierarchy rather than a parallel one. When
 *    a panel carries the whole hierarchy (data-wc-mm-mode="replace") it takes
 *    the place of the drawer item's native sub-menu; otherwise it is appended
 *    alongside it.
 */
(function () {
  'use strict';

  /* =========================================================================
     1. DESKTOP — Products hover coordination
     ========================================================================= */

  function initProductsHover(wrapper) {
    var list   = wrapper.querySelector('.wc-mm-products__list');
    var panels = wrapper.querySelector('.wc-mm-products__panels');
    if (!list || !panels) return;

    var items    = Array.prototype.slice.call(list.querySelectorAll('.wc-mm-products__item'));
    var panelEls = Array.prototype.slice.call(panels.querySelectorAll('.wc-mm-products__panel'));

    if (!items.length || !panelEls.length) return;

    /**
     * Categories that don't point at a collection with products render no
     * panel, so panel N is not necessarily category N. Both sides carry the
     * category's index in the linklist — match on that, not on position.
     */
    function showPanelFor(key) {
      panelEls.forEach(function (p) {
        p.style.display = (p.getAttribute('data-wc-mm-panel') === key) ? 'grid' : 'none';
      });
    }

    // Default: show the first panel that exists.
    var defaultKey = panelEls[0].getAttribute('data-wc-mm-panel');
    showPanelFor(defaultKey);

    items.forEach(function (item) {
      var key = item.getAttribute('data-wc-mm-item');
      item.addEventListener('mouseenter', function () { showPanelFor(key); });
    });

    // When the cursor leaves the whole products widget, revert to the default.
    wrapper.addEventListener('mouseleave', function () { showPanelFor(defaultKey); });
  }

  function initAllProductsHovers() {
    var wrappers = document.querySelectorAll('.wc-mm--products.wc-mm--desktop');
    Array.prototype.forEach.call(wrappers, initProductsHover);
  }

  /* =========================================================================
     2. MOBILE — Panel relocation into the drawer
     ========================================================================= */

  var DRAWER_SELECTOR = '#slideout-mobile-navigation';
  var SOURCE_SELECTOR = '.wc-mm-mobile-panels';
  var NAV_SELECTOR    = '.mobile-menu__navigation';

  var CHEVRON_SVG =
    '<svg role="img" aria-hidden="true" class="testament--apollo-down-carrot details__svg vib-center"' +
    ' height="6px" viewBox="0 0 20 13.3" xmlns="http://www.w3.org/2000/svg">' +
    '<g class="hover-fill" fill="var(--accordion-color)">' +
    '<polygon points="17.7,0 10,8.3 2.3,0 0,2.5 10,13.3 20,2.5 "/>' +
    '</g></svg>';

  function normalize(text) {
    return (text || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * The drawer's top-level menu links — the only ones a mega-menu block can
   * apply to, since the Header matches its blocks against top-level navigation
   * items too. Restricting the search here also keeps a panel from matching a
   * link inside a panel that was already placed.
   */
  function topLevelLinks(drawer) {
    var nav = drawer.querySelector(NAV_SELECTOR);
    if (!nav) return [];

    var rootList = nav.querySelector(':scope > ul.menu__list');
    if (!rootList) return [];

    var links = [];
    Array.prototype.forEach.call(rootList.querySelectorAll(':scope > li'), function (item) {
      var link = item.querySelector(':scope > details > summary .details__link') ||
                 item.querySelector(':scope > a.menu__link');
      if (link) links.push(link);
    });
    return links;
  }

  function findMenuItem(links, panel) {
    var targets = [
      normalize(panel.getAttribute('data-wc-mm-menu-item-mobile')),
      normalize(panel.getAttribute('data-wc-mm-menu-item'))
    ].filter(Boolean);
    if (!targets.length) return null;

    // 1. Exact (normalized) title match.
    for (var t = 0; t < targets.length; t++) {
      for (var i = 0; i < links.length; i++) {
        if (normalize(links[i].textContent) === targets[t]) return links[i];
      }
    }

    // 2. Whole-word containment, e.g. "icons" inside "the icons".
    for (var t2 = 0; t2 < targets.length; t2++) {
      if (targets[t2].length < 3) continue;
      var re = new RegExp('(^|\\s)' + escapeRegExp(targets[t2]) + '($|\\s)');
      for (var j = 0; j < links.length; j++) {
        if (re.test(normalize(links[j].textContent))) return links[j];
      }
    }

    return null;
  }

  /**
   * Wrap a childless drawer item in a <details> so the panel has a level to
   * live in. Mirrors the markup sections/mobile-navigation.liquid emits for
   * items that do have children.
   */
  function wrapInDetails(item, link, panel) {
    var details = document.createElement('details');
    details.className = 'details details--no-padding wc-mm-injected-details';

    var summary = document.createElement('summary');
    summary.className = 'details__summary';

    var anchor = document.createElement('a');
    anchor.className = 'details__link';
    anchor.href = link.getAttribute('href') || '#';
    anchor.textContent = link.textContent.trim();

    var icon = document.createElement('div');
    icon.className = 'details__icon';
    icon.innerHTML = CHEVRON_SVG;

    summary.appendChild(anchor);
    summary.appendChild(icon);

    var content = document.createElement('div');
    content.className = 'details__content';
    content.appendChild(panel);

    details.appendChild(summary);
    details.appendChild(content);

    item.innerHTML = '';
    item.appendChild(details);
  }

  function placePanel(links, panel) {
    var link = findMenuItem(links, panel);
    if (!link) return false;

    panel.classList.add('wc-mm-mobile-panel--placed');

    // Case 1: the item is already an accordion.
    var details = link.closest('details');
    if (details) {
      var content = details.querySelector(':scope > .details__content');
      if (content) {
        // A "replace" panel carries the full hierarchy for this item, straight
        // from the Header blocks the desktop mega-menu is built from — so it
        // stands in for the drawer's own sub-menu instead of duplicating it.
        if (panel.getAttribute('data-wc-mm-mode') === 'replace') {
          content.textContent = '';
        }
        content.appendChild(panel);
        return true;
      }
    }

    // Case 2: the item is a plain link with no sub-menu — give it one.
    var item = link.closest('.menu__item, li');
    if (item) {
      wrapInDetails(item, link, panel);
      return true;
    }

    return false;
  }

  function initMobilePanels() {
    var source = document.querySelector(SOURCE_SELECTOR);
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!source) return;

    if (drawer) {
      var panels = Array.prototype.slice.call(
        source.querySelectorAll('.wc-mm-mobile-panel')
      );
      var links = topLevelLinks(drawer);

      if (links.length) {
        panels.forEach(function (panel) {
          placePanel(links, panel);
        });
      }
    }

    // Remove the now-empty (or leftover) source wrapper.
    if (source.parentNode) {
      source.parentNode.removeChild(source);
    }
  }

  /* =========================================================================
     Init
     ========================================================================= */

  function init() {
    initAllProductsHovers();
    initMobilePanels();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
